import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { addDays, addYears, subYears } from 'date-fns';
import { FormProvider, useWatch } from 'react-hook-form';
import { Box, Callout, Column, Spinner } from 'seagull';
import { PaperclipAttachment } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { TemplateVariablesProvider } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariablesContext';
import { Attachments } from '~/components/Attachments/Attachments';
import { AdHocBillModalOld } from '~/components/BillModal/AdHocBillModalOld/AdHocBillModalOld';
import { isAttachButtonDisabled } from '~/components/BillModal/BillClientModal/Attachments/Attachments.utils';
import { resolveAttachFilesTooltip } from '~/components/BillModal/BillClientModal/BillClientModal.utils';
import { BillModalShell } from '~/components/BillModal/components/BillModalShell/BillModalShell';
import { DateRow } from '~/components/BillModal/components/DateRow/DateRow';
import { BillItemsProvider } from '~/components/BillModal/hooks/useBillItems';
import { useStore, useStoreActions } from '~/contexts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useServiceTemplates } from '~/hooks/reactQuery/queries/useServiceTemplates';
import { DEFAULT_MAX_TOTAL_SIZE, useAttachFiles } from '~/hooks/useAttachFiles';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { isAdHocCharge } from '~/models/invoice-draft';
import { ServiceTemplateType } from '~/models/service-template';
import { useIoContext } from '~/providers/IoContext';
import { selectors } from '~/store/state-selectors';
import { AdHocBillItemsSection } from './AdHocBillItemsSection/AdHocBillItemsSection';
import styles from './AdHocBillModal.module.scss';
import { buildClientContact, restoredChargeToDisabledItem } from './AdHocBillModal.utils';
import { useAdHocBillForm } from './hooks/useAdHocBillForm';
import { useAdHocDateSync } from './hooks/useAdHocDateSync';
import { useAdHocSendActions } from './hooks/useAdHocSendActions';
import { useAdHocSubmit } from './hooks/useAdHocSubmit';
import { EmailContentSection } from './sections/EmailContentSection';
import { PaymentSettingsSection } from './sections/PaymentSettingsSection';
import { ADHOC_TEXT_FIELDS, EMPTY_RICH_TEXT_FIELDS } from './sections/templateVariableFields';
import { SendSplitButton } from './SendSplitButton/SendSplitButton';
const paymentSettingsTextsPath = 'vendorOnboarding.adHocBillModal.paymentSettings';
const emailContentTextsPath = 'vendorOnboarding.adHocBillModal.emailContent';
const EMPTY_INVOICE_DRAFT_DATES = [];
const EMPTY_SERVICE_TEMPLATES = [];
export const GetAdHocBillModalTID = (parentTestId)=>getTestIds(parentTestId);
const AdHocBillModalContent = (param)=>{
    let { relationship, relationshipId, dismissModal, invoiceIssuedTimeoutMs } = param;
    var _useStore, _ref, _ref1;
    const { AdHocBillModalTID } = useMemo(()=>GetAdHocBillModalTID(), []);
    const { t: billTexts } = useLocaleKeys('components.billClientModal');
    const { t: attachFilesTexts } = useLocaleKeys('invoice.attachFiles');
    const { t: adHocTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal');
    const { t: paymentSettingsTexts } = useLocaleKeys(paymentSettingsTextsPath);
    const { t: emailContentTexts } = useLocaleKeys(emailContentTextsPath);
    const businessName = (_useStore = useStore(selectors.user.businessName)) !== null && _useStore !== void 0 ? _useStore : '';
    const worksAt = useStore(selectors.user.business);
    const { api } = useStoreActions();
    const [restoredAttachments, setRestoredAttachments] = useState({
        ids: [],
        issueDate: null
    });
    const restoredAttachmentIds = restoredAttachments.ids;
    const defaultEmailSubject = emailContentTexts.defaultSubject({
        businessName
    });
    const defaultEmailContent = emailContentTexts.defaultContentV2();
    const form = useAdHocBillForm({
        defaultEmailSubject,
        defaultEmailContent,
        initialItems: []
    });
    const fetchScheduledAttachmentUrl = useCallback((attachmentId)=>api.billing.scheduledBillingAttachments.getDownloadUrl(relationshipId, attachmentId), [
        api,
        relationshipId
    ]);
    const removeScheduledAttachment = useCallback(async (attachment)=>{
        if (!restoredAttachments.ids.includes(attachment.id) || restoredAttachments.issueDate == null) return;
        await api.billing.scheduledBillingAttachments.remove(relationshipId, restoredAttachments.issueDate, attachment.id);
        setRestoredAttachments((current)=>_object_spread_props(_object_spread({}, current), {
                ids: current.ids.filter((id)=>id !== attachment.id)
            }));
    }, [
        api,
        relationshipId,
        restoredAttachments
    ]);
    const { attachments, openFilePicker, isUploading, handleRemove, setCurrentAttachments } = useAttachFiles({
        fetchAttachmentUrl: fetchScheduledAttachmentUrl,
        onRemove: removeScheduledAttachment
    });
    const { legalTerms } = useLegalTerms();
    const { data: serviceTemplatesData } = useServiceTemplates(ServiceTemplateType.Service, {
        refreshOnHookMount: true
    });
    const outOfScopeTemplates = (_ref = serviceTemplatesData === null || serviceTemplatesData === void 0 ? void 0 : serviceTemplatesData.active) !== null && _ref !== void 0 ? _ref : EMPTY_SERVICE_TEMPLATES;
    const { getNow } = useIoContext();
    const { issueDateMin, issueDateMax } = useMemo(()=>{
        const now = getNow();
        return {
            issueDateMin: addDays(subYears(now, 1), 1),
            issueDateMax: addYears(now, 1)
        };
    }, [
        getNow
    ]);
    const contact = useMemo(()=>buildClientContact(relationship), [
        relationship
    ]);
    const { isFutureIssueDate } = form;
    const { occupiedDates, isDateOccupied, isScheduledDataLoading, restoredLineItems } = useAdHocDateSync({
        relationshipId,
        form,
        defaultEmailSubject,
        defaultEmailContent,
        setCurrentAttachments,
        setRestoredAttachments
    });
    const restoredDisabledItems = useMemo(()=>restoredLineItems.filter(isAdHocCharge).map(restoredChargeToDisabledItem), [
        restoredLineItems
    ]);
    const { submitAdHoc, isLoading: isSubmitting } = useAdHocSubmit({
        form,
        relationshipId,
        customerId: relationship.customer.id,
        attachments,
        restoredAttachmentIds,
        legalTerms,
        defaultEmailSubject,
        defaultEmailContent,
        dismissModal
    });
    const [includeTermsOfService, coverCreditCardFees, emailSubject, emailContent] = form.watch([
        'includeTermsOfService',
        'coverCreditCardFees',
        ...ADHOC_TEXT_FIELDS
    ]);
    const templateVarTextFields = useMemo(()=>[
            emailSubject,
            emailContent
        ], [
        emailSubject,
        emailContent
    ]);
    const { handleSendOption } = useAdHocSendActions({
        relationship,
        relationshipId,
        form,
        coverCreditCardFees,
        submitAdHoc,
        dismissModal,
        invoiceIssuedTimeoutMs
    });
    const watchedItems = useWatch({
        control: form.control,
        name: 'items'
    });
    const mainAction = /*#__PURE__*/ _jsx(SendSplitButton, {
        isFutureIssueDate: isFutureIssueDate,
        disabled: isSubmitting || ((_ref1 = watchedItems === null || watchedItems === void 0 ? void 0 : watchedItems.length) !== null && _ref1 !== void 0 ? _ref1 : 0) === 0 && restoredLineItems.length === 0,
        onSelect: handleSendOption,
        testId: AdHocBillModalTID.SendSplitButton()
    });
    const attachFilesDisabled = isAttachButtonDisabled({
        attachments,
        isUploading,
        maxTotalSize: DEFAULT_MAX_TOTAL_SIZE
    });
    const attachFilesAction = {
        text: attachFilesTexts.button(),
        iconStart: /*#__PURE__*/ _jsx(PaperclipAttachment, {
            size: 16
        }),
        disabled: attachFilesDisabled,
        tooltipTitle: resolveAttachFilesTooltip({
            isUploading,
            isDisabled: attachFilesDisabled,
            texts: {
                uploading: attachFilesTexts.tooltipUploading(),
                exceededLimit: attachFilesTexts.tooltipExceededLimit({
                    maxSizeMB: DEFAULT_MAX_TOTAL_SIZE / (1024 * 1024)
                })
            }
        }),
        bi: {
            name: 'attach_files'
        },
        testId: AdHocBillModalTID.HeaderAttachFiles(),
        onClick: openFilePicker
    };
    return /*#__PURE__*/ _jsx(BillModalShell, {
        testId: AdHocBillModalTID.Root(),
        title: billTexts.billClientTitle({
            customerName: relationship.customer.name
        }),
        mainAction: mainAction,
        additionalActions: {
            inline: [
                attachFilesAction
            ]
        },
        onClose: dismissModal,
        children: worksAt == null || isScheduledDataLoading ? /*#__PURE__*/ _jsx(Box, {
            className: styles.loadingWrapper,
            testId: AdHocBillModalTID.LoadingSpinner(),
            children: /*#__PURE__*/ _jsx(Spinner, {
                size: 48
            })
        }) : /*#__PURE__*/ _jsx(TemplateVariablesProvider, {
            contact: contact,
            worksAt: worksAt,
            agreementName: null,
            effectiveDate: null,
            initialTemplateVariableValues: undefined,
            textFields: templateVarTextFields,
            richTextFields: EMPTY_RICH_TEXT_FIELDS,
            children: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                children: /*#__PURE__*/ _jsx(BillItemsProvider, {
                    children: /*#__PURE__*/ _jsx(Box, {
                        className: styles.contentPage,
                        children: /*#__PURE__*/ _jsxs(Column, {
                            gap: 16,
                            className: styles.modalContentWrapper,
                            children: [
                                /*#__PURE__*/ _jsx(Box, {
                                    className: styles.headerRow,
                                    children: /*#__PURE__*/ _jsx(DateRow, {
                                        name: "issueDate",
                                        compactInput: true,
                                        invoiceDraftDates: EMPTY_INVOICE_DRAFT_DATES,
                                        highlightDays: occupiedDates,
                                        highlightTooltip: adHocTexts.occupiedDateCallout(),
                                        minDate: issueDateMin,
                                        maxDate: issueDateMax,
                                        bi: {
                                            name: 'ad_hoc_issue_date'
                                        },
                                        testId: AdHocBillModalTID.IssueDate()
                                    })
                                }),
                                isDateOccupied && /*#__PURE__*/ _jsx(Callout, {
                                    intent: "info",
                                    testId: AdHocBillModalTID.OccupiedDateCallout(),
                                    children: adHocTexts.occupiedDateCallout()
                                }),
                                /*#__PURE__*/ _jsx(AdHocBillItemsSection, {
                                    relationship: relationship,
                                    outOfScopeTemplates: outOfScopeTemplates,
                                    addChargeLabel: billTexts.addCharge.triggerLabel(),
                                    disabledItems: restoredDisabledItems,
                                    disabledRowTooltip: adHocTexts.restoredChargeTooltip(),
                                    testIds: AdHocBillModalTID
                                }),
                                /*#__PURE__*/ _jsxs(Box, {
                                    wrap: true,
                                    className: styles.sectionsRow,
                                    children: [
                                        /*#__PURE__*/ _jsxs(Column, {
                                            gap: 24,
                                            className: styles.sectionColumn,
                                            children: [
                                                /*#__PURE__*/ _jsx(PaymentSettingsSection, {
                                                    testIds: AdHocBillModalTID.PaymentSettings,
                                                    showTermsOfService: includeTermsOfService,
                                                    texts: paymentSettingsTexts
                                                }),
                                                attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                                                    files: attachments,
                                                    onRemove: handleRemove,
                                                    disableDownloadLinks: true
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(EmailContentSection, {
                                            testIds: AdHocBillModalTID.EmailContent,
                                            texts: emailContentTexts,
                                            defaultEmailSubject: defaultEmailSubject,
                                            defaultEmailContent: defaultEmailContent
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                })
            }))
        })
    });
};
const AdHocBillModalBody = (props)=>{
    const { AdHocBillModalTID } = useMemo(()=>GetAdHocBillModalTID(), []);
    const { relationship } = useRelationship(props.relationshipId);
    if (relationship == null) {
        return /*#__PURE__*/ _jsx(BillModalShell, {
            testId: AdHocBillModalTID.Root(),
            title: "",
            onClose: props.dismissModal,
            children: /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingWrapper,
                testId: AdHocBillModalTID.LoadingSpinner(),
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 48
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(AdHocBillModalContent, _object_spread_props(_object_spread({}, props), {
        relationship: relationship
    }));
};
export const AdHocBillModal = (props)=>{
    if (!useFeatureFlags().BILL_MODAL_V2) return /*#__PURE__*/ _jsx(AdHocBillModalOld, _object_spread({}, props));
    return /*#__PURE__*/ _jsx(AdHocBillModalBody, _object_spread({}, props));
};
